import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, Eye, Clock, CheckCircle, Rocket, FileText, Upload, Download, 
  User, Phone, Mail, CreditCard, Banknote, FileCheck, Shield, RefreshCw,
  ExternalLink, AlertTriangle, MessageCircle, Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatDate, getWhatsAppLink } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

// Agent WhatsApp number for notifications
const AGENT_WHATSAPP = '+243842217277';

const statusConfig = {
  initiated: { label: 'Initiée', color: 'bg-gray-500/20 text-gray-400', icon: Clock, step: 1 },
  received: { label: 'Paiement reçu', color: 'bg-blue-500/20 text-blue-400', icon: FileCheck, step: 2 },
  launched: { label: 'Docs téléchargés', color: 'bg-yellow-500/20 text-yellow-400', icon: Rocket, step: 3 },
  processed: { label: 'Visa prêt', color: 'bg-purple-500/20 text-purple-400', icon: Shield, step: 4 },
  completed: { label: 'Terminée', color: 'bg-green-500/20 text-green-400', icon: CheckCircle, step: 5 }
};

export const VisaApplicationsPage = () => {
  const { isAdmin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [agents, setAgents] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(null); // 'payment_confirmed' | 'visa_ready' | null
  const visaUploadRef = useRef(null);
  const insuranceUploadRef = useRef(null);

  // Generate WhatsApp messages
  const getWhatsAppMessages = (app) => {
    const ref = app?.id?.slice(0, 8).toUpperCase();
    const downloadLink = `${window.location.origin}/visa/dubai/download/${app?.id}`;
    
    return {
      // Message to AGENT when payment is confirmed
      agentNewApplication: `📥 *NOUVELLE DEMANDE VISA DUBAÏ*

Référence: ${ref}
Client: ${app?.applicant_name}
Téléphone: ${app?.applicant_phone}
Type: ${app?.visa_type || 'Visa Visiteur'}

💳 Paiement confirmé (Espèces)

Connectez-vous à l'admin pour télécharger les documents du client.

Global Wings Travel Consulting`,

      // Message to CLIENT when visa is ready
      clientVisaReady: `🎉 *VISA PRÊT - Global Wings*

Bonjour ${app?.applicant_name},

Excellente nouvelle ! Votre visa pour Dubaï est prêt !

📋 Référence: ${ref}

📥 Téléchargez vos documents ici:
${downloadLink}

Documents disponibles:
✅ Visa électronique
✅ Assurance voyage

Bon voyage ! ✈️
Global Wings Travel Consulting`
    };
  };

  useEffect(() => {
    fetchApplications();
    if (isAdmin()) fetchAgents();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await api.get('/visa-applications');
      setApplications(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/admin/users');
      setAgents(response.data.filter(u => u.role === 'agent'));
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const refreshSelectedApp = async (appId) => {
    try {
      const response = await api.get(`/visa-applications/${appId}`);
      setSelectedApp(response.data);
      fetchApplications();
    } catch (error) {
      console.error('Error refreshing app:', error);
    }
  };

  // Confirm cash payment - AUTO changes status to 'received'
  const confirmPayment = async () => {
    if (!selectedApp) return;
    try {
      await api.patch(`/visa-applications/${selectedApp.id}/confirm-payment?payment_method=cash`);
      toast.success('Paiement confirmé ! Statut: Reçu');
      setShowWhatsAppPrompt('payment_confirmed');
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur de confirmation');
    }
  };

  // Download client documents - AUTO changes status to 'launched'
  const downloadClientDocs = async () => {
    if (!selectedApp) return;
    setDownloading(true);
    try {
      const response = await api.get(
        `/visa-applications/${selectedApp.id}/download-client-docs`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documents_client_${selectedApp.id.slice(0,8).toUpperCase()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Documents téléchargés ! Statut: Lancé');
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  // Upload visa/insurance - AUTO changes status to 'processed' when both uploaded
  const handleAgentUpload = async (file, documentType) => {
    if (!selectedApp || !file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/visa-applications/${selectedApp.id}/agent-upload?document_type=${documentType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.status_updated) {
        toast.success('Documents prêts ! Envoyez le lien au client via WhatsApp');
        setShowWhatsAppPrompt('visa_ready');
      } else {
        toast.success(`${documentType === 'visa' ? 'Visa' : 'Assurance'} uploadé(e)`);
      }
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/visa-applications/${selectedApp.id}/notes?note=${encodeURIComponent(newNote)}`);
      toast.success('Note ajoutée');
      setNewNote('');
      refreshSelectedApp(selectedApp.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const assignAgent = async (appId, agentId) => {
    try {
      await api.patch(`/visa-applications/${appId}/assign?agent_id=${agentId}`);
      toast.success('Agent assigné');
      fetchApplications();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.applicant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicant_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesDestination = destinationFilter === 'all' || 
      (destinationFilter === 'dubai' && app.destination?.toLowerCase().includes('duba')) ||
      (destinationFilter === 'other' && !app.destination?.toLowerCase().includes('duba'));
    return matchesSearch && matchesStatus && matchesDestination;
  });

  const isDubaiVisa = (app) => app?.destination?.toLowerCase().includes('duba');

  const getAgentDocs = (app) => {
    const docs = app?.agent_documents || [];
    return {
      visa: docs.find(d => d.document_type === 'visa'),
      insurance: docs.find(d => d.document_type === 'insurance')
    };
  };

  const getNextAction = (app) => {
    if (!isDubaiVisa(app)) return null;
    switch (app.status) {
      case 'initiated': return { label: 'Confirmer paiement', action: 'confirm_payment' };
      case 'received': return { label: 'Télécharger docs client', action: 'download_docs' };
      case 'launched': return { label: 'Uploader visa & assurance', action: 'upload_docs' };
      case 'processed': return { label: 'En attente téléchargement client', action: 'waiting' };
      case 'completed': return { label: 'Dossier terminé', action: 'done' };
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="visa-applications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Demandes de Visa</h2>
          <p className="text-gray-400">{applications.length} demandes au total</p>
        </div>
        <Button onClick={fetchApplications} variant="outline" className="border-white/10 text-gray-300 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      {/* Workflow Legend for Dubai */}
      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardContent className="p-4">
          <p className="text-amber-400 text-sm font-medium mb-3">📋 Workflow Automatisé Visa Dubaï</p>
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(statusConfig).map(([key, config]) => (
              <div key={key} className="flex items-center gap-1">
                <span className={`px-2 py-1 rounded ${config.color}`}>{config.step}. {config.label}</span>
                {config.step < 5 && <span className="text-gray-500">→</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white"
              />
            </div>
            <Select value={destinationFilter} onValueChange={setDestinationFilter}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Destination" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10">
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="dubai">Dubaï</SelectItem>
                <SelectItem value="other">Autres</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10">
                <SelectItem value="all">Tous</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-0">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune demande trouvée</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {filteredApplications.map((app) => {
                const StatusIcon = statusConfig[app.status]?.icon || Clock;
                const isDubai = isDubaiVisa(app);
                const nextAction = getNextAction(app);
                return (
                  <div 
                    key={app.id}
                    className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => { setSelectedApp(app); setShowDetail(true); }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isDubai ? 'bg-amber-500/20' : 'bg-sky-500/20'
                        }`}>
                          <StatusIcon className={`w-6 h-6 ${statusConfig[app.status]?.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{app.applicant_name}</p>
                            {isDubai && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Dubaï</Badge>}
                          </div>
                          <p className="text-sm text-gray-400">{app.destination} - {app.visa_type}</p>
                          {isDubai && nextAction && nextAction.action !== 'done' && (
                            <p className="text-xs text-amber-400 mt-1">→ {nextAction.label}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={statusConfig[app.status]?.color}>
                          {statusConfig[app.status]?.label}
                        </Badge>
                        <span className="text-sm text-gray-500 hidden md:block">{formatDate(app.created_at)}</span>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDubaiVisa(selectedApp) ? 'bg-amber-500/20' : 'bg-sky-500/20'
              }`}>
                <FileText className={`w-5 h-5 ${isDubaiVisa(selectedApp) ? 'text-amber-400' : 'text-sky-400'}`} />
              </div>
              <div>
                <span>Visa {selectedApp?.destination}</span>
                {isDubaiVisa(selectedApp) && (
                  <Badge className="ml-2 bg-amber-500/20 text-amber-400">Workflow Auto</Badge>
                )}
              </div>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Réf: {selectedApp?.id?.slice(0, 8).toUpperCase()}
            </DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-6 py-4">
              {/* Status Progress for Dubai */}
              {isDubaiVisa(selectedApp) && (
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-amber-400 text-sm font-medium mb-3">Progression du Workflow</p>
                  <div className="flex items-center justify-between">
                    {Object.entries(statusConfig).map(([key, config], idx) => {
                      const isActive = config.step <= statusConfig[selectedApp.status]?.step;
                      const isCurrent = key === selectedApp.status;
                      return (
                        <div key={key} className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            isActive ? 'bg-amber-500 text-white' : 'bg-white/10 text-gray-500'
                          } ${isCurrent ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0F172A]' : ''}`}>
                            {config.step}
                          </div>
                          {idx < 4 && (
                            <div className={`w-8 h-1 ${isActive ? 'bg-amber-500' : 'bg-white/10'}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                    <span>Initiée</span>
                    <span>Reçue</span>
                    <span>Lancée</span>
                    <span>Prête</span>
                    <span>Terminée</span>
                  </div>
                </div>
              )}

              {/* Dubai Workflow Actions */}
              {isDubaiVisa(selectedApp) && (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-amber-400" /> Action Requise
                  </h4>
                  
                  {/* Step 1: Confirm Payment */}
                  {selectedApp.status === 'initiated' && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-sm text-white mb-2">
                        <AlertTriangle className="w-4 h-4 inline mr-1 text-blue-400" />
                        Le client a soumis sa demande. Confirmez la réception du paiement en espèces.
                      </p>
                      <Button onClick={confirmPayment} className="w-full bg-blue-600 hover:bg-blue-700">
                        <Banknote className="w-4 h-4 mr-2" /> Confirmer Paiement Espèces
                      </Button>
                    </div>
                  )}

                  {/* WhatsApp Prompt after Payment Confirmed */}
                  {showWhatsAppPrompt === 'payment_confirmed' && selectedApp.status === 'received' && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-3">
                      <p className="text-sm text-green-400">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Paiement confirmé ! Informez l'équipe via WhatsApp :
                      </p>
                      <a
                        href={getWhatsAppLink(AGENT_WHATSAPP, getWhatsAppMessages(selectedApp).agentNewApplication)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Envoyer WhatsApp à l'Agent
                      </a>
                      <button 
                        onClick={() => setShowWhatsAppPrompt(null)}
                        className="w-full text-xs text-gray-400 hover:text-white"
                      >
                        Passer cette étape
                      </button>
                    </div>
                  )}

                  {/* Step 2: Download Client Docs */}
                  {selectedApp.status === 'received' && !showWhatsAppPrompt && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-sm text-white mb-2">
                        <AlertTriangle className="w-4 h-4 inline mr-1 text-yellow-400" />
                        Paiement confirmé ! Téléchargez les documents du client pour lancer la procédure.
                      </p>
                      <Button onClick={downloadClientDocs} disabled={downloading} className="w-full bg-yellow-600 hover:bg-yellow-700">
                        {downloading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Télécharger Documents Client
                      </Button>
                    </div>
                  )}

                  {/* Step 3: Upload Visa & Insurance */}
                  {selectedApp.status === 'launched' && (
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <p className="text-sm text-white mb-3">
                        <AlertTriangle className="w-4 h-4 inline mr-1 text-purple-400" />
                        Documents client téléchargés. Uploadez le visa et l'assurance obtenus.
                      </p>
                      
                      <input ref={visaUploadRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleAgentUpload(e.target.files[0], 'visa')} />
                      <input ref={insuranceUploadRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleAgentUpload(e.target.files[0], 'insurance')} />
                      
                      <div className="space-y-2">
                        {getAgentDocs(selectedApp).visa ? (
                          <div className="flex items-center gap-2 p-2 rounded bg-green-500/10 text-green-400 text-sm">
                            <CheckCircle className="w-4 h-4" /> Visa uploadé
                          </div>
                        ) : (
                          <Button onClick={() => visaUploadRef.current?.click()} disabled={uploading}
                            className="w-full bg-purple-600 hover:bg-purple-700">
                            <Upload className="w-4 h-4 mr-2" /> Uploader le VISA
                          </Button>
                        )}
                        
                        {getAgentDocs(selectedApp).insurance ? (
                          <div className="flex items-center gap-2 p-2 rounded bg-green-500/10 text-green-400 text-sm">
                            <CheckCircle className="w-4 h-4" /> Assurance uploadée
                          </div>
                        ) : (
                          <Button onClick={() => insuranceUploadRef.current?.click()} disabled={uploading}
                            variant="outline" className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                            <Upload className="w-4 h-4 mr-2" /> Uploader l'ASSURANCE
                          </Button>
                        )}
                      </div>
                      
                      {getAgentDocs(selectedApp).visa && getAgentDocs(selectedApp).insurance && !showWhatsAppPrompt && (
                        <p className="text-green-400 text-sm mt-2 text-center">
                          ✅ Les deux documents sont uploadés !
                        </p>
                      )}
                    </div>
                  )}

                  {/* WhatsApp Prompt after Visa Ready */}
                  {showWhatsAppPrompt === 'visa_ready' && selectedApp.status === 'processed' && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-3">
                      <p className="text-sm text-green-400">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Visa et Assurance prêts ! Envoyez le lien de téléchargement au client :
                      </p>
                      <a
                        href={getWhatsAppLink(selectedApp.applicant_phone, getWhatsAppMessages(selectedApp).clientVisaReady)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Envoyer WhatsApp au Client
                      </a>
                      <p className="text-xs text-gray-400 text-center">
                        Client: {selectedApp.applicant_name} ({selectedApp.applicant_phone})
                      </p>
                      <button 
                        onClick={() => setShowWhatsAppPrompt(null)}
                        className="w-full text-xs text-gray-400 hover:text-white"
                      >
                        Passer cette étape
                      </button>
                    </div>
                  )}

                  {/* Step 4: Waiting for Client */}
                  {selectedApp.status === 'processed' && !showWhatsAppPrompt && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 space-y-3">
                      <p className="text-sm text-green-400">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Visa et assurance prêts ! En attente du téléchargement par le client.
                      </p>
                      <a
                        href={getWhatsAppLink(selectedApp.applicant_phone, getWhatsAppMessages(selectedApp).clientVisaReady)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
                      >
                        <Send className="w-4 h-4" />
                        Renvoyer le lien WhatsApp au client
                      </a>
                      <p className="text-xs text-gray-400 text-center">
                        Le statut passera à "Terminée" quand le client télécharge ses documents.
                      </p>
                    </div>
                  )}

                  {/* Step 5: Completed */}
                  {selectedApp.status === 'completed' && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <p className="text-sm text-green-400">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Dossier terminé ! Le client a téléchargé ses documents.
                      </p>
                      {selectedApp.completed_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          Complété le {formatDate(selectedApp.completed_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Applicant Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-400">Nom</Label>
                  <div className="flex items-center gap-2 text-white">
                    <User className="w-4 h-4 text-gray-400" />
                    {selectedApp.applicant_name}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400">Email</Label>
                  <div className="flex items-center gap-2 text-white">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {selectedApp.applicant_email}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400">Téléphone</Label>
                  <div className="flex items-center gap-2 text-white">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {selectedApp.applicant_phone}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400">Type de Visa</Label>
                  <div className="flex items-center gap-2 text-white">
                    <FileText className="w-4 h-4 text-gray-400" />
                    {selectedApp.visa_type}
                  </div>
                </div>
              </div>

              {/* Agent Assignment */}
              {isAdmin() && (
                <div className="p-4 rounded-lg bg-white/5">
                  <Label className="text-gray-400">Assigner à un agent</Label>
                  <Select value={selectedApp.assigned_agent || ''} 
                    onValueChange={(value) => assignAgent(selectedApp.id, value)}>
                    <SelectTrigger className="mt-2 bg-white/10 border-white/10">
                      <SelectValue placeholder="Sélectionner un agent" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10">
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-4">
                <Label className="text-gray-400">Notes</Label>
                {selectedApp.notes?.length > 0 && (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedApp.notes.map((note, index) => (
                      <div key={index} className="p-2 rounded-lg bg-white/5 text-sm">
                        <p className="text-white">{note.content}</p>
                        <p className="text-xs text-gray-500 mt-1">{note.author} - {formatDate(note.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea placeholder="Ajouter une note..." value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="bg-white/5 border-white/10 text-white" rows={2} />
                  <Button onClick={addNote} className="bg-sky-500 hover:bg-sky-600">Ajouter</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisaApplicationsPage;
